import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API = "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";
const MAX_MASKS = 8;
const MIN_MASK_SIZE = 0.02;
const MIN_POLYGON_AREA = MIN_MASK_SIZE * MIN_MASK_SIZE;
const MAX_POLYGON_POINTS = 24;
const DEFAULT_CONFIDENCE_THRESHOLD = 40;
const MIN_CONFIDENCE_THRESHOLD = 25;
const MAX_CONFIDENCE_THRESHOLD = 100;
const CLOSE_POINT_DISTANCE = 0.025;
const GEOMETRY_EPSILON = 1e-9;

export const ANALYTICS_OBJECT_OPTIONS = [
  { value: "person", label: "Person" },
  { value: "car", label: "Car" },
  { value: "bicycle", label: "Bicycle" },
  { value: "boat", label: "Boat" },
  { value: "animal", label: "Animal" },
  { value: "loitering", label: "Loitering" },
] as const;

const DEFAULT_LOITERING_SECONDS = 60;
const MIN_LOITERING_SECONDS = 10;
const MAX_LOITERING_SECONDS = 3600;

export type AnalyticsObjectType = (typeof ANALYTICS_OBJECT_OPTIONS)[number]["value"];
export type AnalyticsPoint = { x: number; y: number };

type AnalyticsMaskBase = {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  objectTypes: AnalyticsObjectType[];
  loiteringSeconds?: number;
};

export type AnalyticsRectangleMask = AnalyticsMaskBase;
export type AnalyticsPolygonMask = AnalyticsMaskBase & {
  type: "polygon";
  points: AnalyticsPoint[];
};
export type AnalyticsMask = AnalyticsRectangleMask | AnalyticsPolygonMask;

export type CameraAnalyticsConfig = {
  version: 2;
  enabled: boolean;
  detectionMode: "per_mask_objects";
  confidenceThreshold: number;
  objectTypes: AnalyticsObjectType[];
  masks: AnalyticsMask[];
  selectedMaskIds: string[];
};

type Camera = {
  cameraId: string;
  name?: string;
  thumbnailUrl?: string;
  analyticsConfig?: Partial<CameraAnalyticsConfig> & { version?: number; detectionMode?: string };
  [key: string]: unknown;
};

type Props = { camera: Camera; onUpdateCamera: (camera: any) => void };

const buttonStyle: React.CSSProperties = {
  minHeight: 38,
  padding: "8px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  color: "#334155",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
};

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}
function round(value: number) {
  return Math.round(value * 1_000_000) / 1_000_000;
}
function normalizeObjects(value: unknown): AnalyticsObjectType[] {
  const requested = Array.isArray(value) ? value.map(String) : [];
  return ANALYTICS_OBJECT_OPTIONS.filter((option) => requested.includes(option.value)).map(
    (option) => option.value,
  );
}
function polygonArea(points: AnalyticsPoint[]) {
  let total = 0;
  for (let index = 0; index < points.length; index += 1) {
    const next = points[(index + 1) % points.length];
    total += points[index].x * next.y - next.x * points[index].y;
  }
  return Math.abs(total) / 2;
}
function orientation(a: AnalyticsPoint, b: AnalyticsPoint, c: AnalyticsPoint) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}
function onSegment(a: AnalyticsPoint, b: AnalyticsPoint, point: AnalyticsPoint) {
  return Math.abs(orientation(a, b, point)) <= GEOMETRY_EPSILON &&
    point.x >= Math.min(a.x, b.x) - GEOMETRY_EPSILON &&
    point.x <= Math.max(a.x, b.x) + GEOMETRY_EPSILON &&
    point.y >= Math.min(a.y, b.y) - GEOMETRY_EPSILON &&
    point.y <= Math.max(a.y, b.y) + GEOMETRY_EPSILON;
}
function segmentsIntersect(a: AnalyticsPoint, b: AnalyticsPoint, c: AnalyticsPoint, d: AnalyticsPoint) {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (
    ((abC > GEOMETRY_EPSILON && abD < -GEOMETRY_EPSILON) || (abC < -GEOMETRY_EPSILON && abD > GEOMETRY_EPSILON)) &&
    ((cdA > GEOMETRY_EPSILON && cdB < -GEOMETRY_EPSILON) || (cdA < -GEOMETRY_EPSILON && cdB > GEOMETRY_EPSILON))
  ) return true;
  return onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b);
}
function polygonSelfIntersects(points: AnalyticsPoint[]) {
  for (let left = 0; left < points.length; left += 1) {
    const leftNext = (left + 1) % points.length;
    for (let right = left + 1; right < points.length; right += 1) {
      const rightNext = (right + 1) % points.length;
      if (leftNext === right || rightNext === left || (left === 0 && rightNext === 0)) continue;
      if (segmentsIntersect(points[left], points[leftNext], points[right], points[rightNext])) return true;
    }
  }
  return false;
}
function polygonGeometry(rawPoints: AnalyticsPoint[]): Omit<AnalyticsPolygonMask, "id" | "name" | "objectTypes" | "loiteringSeconds"> | null {
  if (rawPoints.length < 3 || rawPoints.length > MAX_POLYGON_POINTS) return null;
  const points: AnalyticsPoint[] = [];
  for (const raw of rawPoints) {
    const x = Number(raw?.x);
    const y = Number(raw?.y);
    if (![x, y].every(Number.isFinite) || x < 0 || x > 1 || y < 0 || y > 1) return null;
    points.push({ x: round(x), y: round(y) });
  }
  if (new Set(points.map((point) => `${point.x}:${point.y}`)).size !== points.length) return null;
  if (polygonSelfIntersects(points)) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  const width = Math.max(...xs) - x;
  const height = Math.max(...ys) - y;
  if (width < MIN_MASK_SIZE || height < MIN_MASK_SIZE || polygonArea(points) < MIN_POLYGON_AREA) return null;
  return {
    type: "polygon",
    points,
    x: round(x),
    y: round(y),
    width: round(width),
    height: round(height),
  };
}
function normalizeMask(value: unknown, index: number, fallbackObjects: AnalyticsObjectType[]): AnalyticsMask | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  const ownObjects = normalizeObjects(source.objectTypes);
  const common = {
    id: String(source.id || `mask-${index + 1}`),
    name: String(source.name || `Detection area ${index + 1}`),
    objectTypes: ownObjects.length ? ownObjects : fallbackObjects,
    loiteringSeconds: clamp(Math.round(Number(source.loiteringSeconds) || DEFAULT_LOITERING_SECONDS), MIN_LOITERING_SECONDS, MAX_LOITERING_SECONDS),
  };
  if (source.type === "polygon" || Array.isArray(source.points)) {
    const rawPoints = (Array.isArray(source.points) ? source.points : []).map((raw) => {
      const point = raw && typeof raw === "object" && !Array.isArray(raw) ? raw as Record<string, unknown> : {};
      return { x: Number(point.x), y: Number(point.y) };
    });
    const geometry = polygonGeometry(rawPoints);
    return geometry ? { ...common, ...geometry } : null;
  }
  const x = clamp(Number(source.x));
  const y = clamp(Number(source.y));
  const width = clamp(Number(source.width), 0, 1 - x);
  const height = clamp(Number(source.height), 0, 1 - y);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width < MIN_MASK_SIZE || height < MIN_MASK_SIZE) return null;
  return {
    ...common,
    x: round(x),
    y: round(y),
    width: round(width),
    height: round(height),
  };
}

export function normalizeAnalyticsConfig(value: unknown): CameraAnalyticsConfig {
  const candidate = value && typeof value === "object" && !Array.isArray(value)
    ? (value as Partial<CameraAnalyticsConfig>)
    : {};
  const legacyObjects = normalizeObjects(candidate.objectTypes);
  const masks = (Array.isArray(candidate.masks) ? candidate.masks : [])
    .map((mask, index) => normalizeMask(mask, index, legacyObjects))
    .filter((mask): mask is AnalyticsMask => Boolean(mask))
    .slice(0, MAX_MASKS);
  const requestedIds = Array.isArray(candidate.selectedMaskIds)
    ? new Set(candidate.selectedMaskIds.map(String))
    : new Set(masks.map((mask) => mask.id));
  const selectedMaskIds = masks.filter((mask) => requestedIds.has(mask.id)).map((mask) => mask.id);
  const objectTypes = ANALYTICS_OBJECT_OPTIONS.filter((option) =>
    masks.some((mask) => mask.objectTypes.includes(option.value)),
  ).map((option) => option.value);
  const confidenceThreshold = clamp(
    Math.round(Number(candidate.confidenceThreshold) || DEFAULT_CONFIDENCE_THRESHOLD),
    MIN_CONFIDENCE_THRESHOLD,
    MAX_CONFIDENCE_THRESHOLD,
  );
  return { version: 2, enabled: candidate.enabled !== false, detectionMode: "per_mask_objects", confidenceThreshold, objectTypes, masks, selectedMaskIds };
}

function ObjectChips({ values, disabled, onChange }: { values: AnalyticsObjectType[]; disabled?: boolean; onChange: (values: AnalyticsObjectType[]) => void }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {ANALYTICS_OBJECT_OPTIONS.map((option) => {
        const selected = values.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onChange(selected
              ? values.filter((value) => value !== option.value)
              : ANALYTICS_OBJECT_OPTIONS.filter((item) => values.includes(item.value) || item.value === option.value).map((item) => item.value))}
            style={{ ...buttonStyle, minHeight: 34, padding: "6px 11px", borderColor: selected ? "#2563eb" : "#cbd5e1", color: selected ? "#1d4ed8" : "#64748b", background: selected ? "#eff6ff" : "#fff", opacity: disabled ? 0.55 : 1 }}
          >
            {selected ? "✓ " : ""}{option.label}
          </button>
        );
      })}
    </div>
  );
}

async function getIdToken() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Your session expired. Sign in again.");
  return token;
}
async function readResponse(response: Response) {
  const text = await response.text();
  try { return { text, json: text ? JSON.parse(text) : null }; } catch { return { text, json: null }; }
}
function newMaskId() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? `mask-${crypto.randomUUID()}` : `mask-${Date.now()}`;
}
function nextName(masks: AnalyticsMask[]) {
  let i = 1;
  const names = new Set(masks.map((mask) => mask.name));
  while (names.has(`Detection area ${i}`)) i += 1;
  return `Detection area ${i}`;
}
function svgPoints(points: AnalyticsPoint[]) {
  return points.map((point) => `${round(point.x * 1000)},${round(point.y * 1000)}`).join(" ");
}

export default function AnalyticsTab({ camera, onUpdateCamera }: Props) {
  const initial = useMemo(() => normalizeAnalyticsConfig(camera.analyticsConfig), [camera.cameraId, camera.analyticsConfig]);
  const [config, setConfig] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [defaultObjects, setDefaultObjects] = useState<AnalyticsObjectType[]>(initial.objectTypes.length ? initial.objectTypes : ["person"]);
  const [drawing, setDrawing] = useState(false);
  const [redrawingMaskId, setRedrawingMaskId] = useState<string | null>(null);
  const [draftPoints, setDraftPoints] = useState<AnalyticsPoint[]>([]);
  const [hoverPoint, setHoverPoint] = useState<AnalyticsPoint | null>(null);
  const [aspectRatio, setAspectRatio] = useState("16 / 9");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    const next = normalizeAnalyticsConfig(camera.analyticsConfig);
    setConfig(next); setSaved(next);
    setDefaultObjects(next.objectTypes.length ? next.objectTypes : ["person"]);
    setDrawing(false); setRedrawingMaskId(null); setDraftPoints([]); setHoverPoint(null); setMessage(""); setError(false);
  }, [camera.cameraId, camera.analyticsConfig]);

  const selectedSet = useMemo(() => new Set(config.selectedMaskIds), [config.selectedMaskIds]);
  const isDirty = JSON.stringify(config) !== JSON.stringify(saved);
  const validationMessage = !config.enabled ? "" : !config.selectedMaskIds.length
    ? "Draw and enable at least one detection area."
    : config.masks.find((mask) => selectedSet.has(mask.id) && mask.objectTypes.length === 0)?.name
      ? `${config.masks.find((mask) => selectedSet.has(mask.id) && mask.objectTypes.length === 0)?.name} needs at least one object type.`
      : "";

  function canvasPoint(event: React.PointerEvent<HTMLDivElement>): AnalyticsPoint {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width || !box.height) return { x: 0, y: 0 };
    return { x: round(clamp((event.clientX - box.left) / box.width)), y: round(clamp((event.clientY - box.top) / box.height)) };
  }
  function startDrawing(maskId: string | null = null) {
    if (!config.enabled || saving) return;
    if (!maskId && config.masks.length >= MAX_MASKS) return;
    setDrawing(true);
    setRedrawingMaskId(maskId);
    setDraftPoints([]);
    setHoverPoint(null);
    setError(false);
    setMessage(`Click 3-${MAX_POLYGON_POINTS} points around the exact detection area. Click the first point again to close it.`);
  }
  function cancelDrawing() {
    setDrawing(false);
    setRedrawingMaskId(null);
    setDraftPoints([]);
    setHoverPoint(null);
    setMessage("");
    setError(false);
  }
  function finishDrawing() {
    const geometry = polygonGeometry(draftPoints);
    if (!geometry) {
      setError(true);
      setMessage("The detection polygon needs 3-24 distinct, non-crossing points and must cover a visible area.");
      return;
    }
    if (redrawingMaskId) {
      setConfig((current) => ({
        ...current,
        masks: current.masks.map((mask) => mask.id === redrawingMaskId ? {
          id: mask.id,
          name: mask.name,
          ...geometry,
          objectTypes: mask.objectTypes,
          loiteringSeconds: mask.loiteringSeconds,
        } : mask),
      }));
      const maskName = config.masks.find((mask) => mask.id === redrawingMaskId)?.name || "Detection area";
      setMessage(`${maskName} redrawn. Save analytics to apply the polygon.`);
    } else {
      const id = newMaskId();
      const mask: AnalyticsPolygonMask = {
        id,
        name: nextName(config.masks),
        ...geometry,
        objectTypes: defaultObjects.length ? defaultObjects : ["person"],
        loiteringSeconds: DEFAULT_LOITERING_SECONDS,
      };
      setConfig((current) => ({
        ...current,
        masks: [...current.masks, mask],
        selectedMaskIds: [...current.selectedMaskIds, mask.id],
        objectTypes: Array.from(new Set([...current.objectTypes, ...mask.objectTypes])) as AnalyticsObjectType[],
      }));
      setMessage(`${mask.name} added. Choose its objects below.`);
    }
    setDrawing(false);
    setRedrawingMaskId(null);
    setDraftPoints([]);
    setHoverPoint(null);
    setError(false);
  }
  function addPolygonPoint(event: React.PointerEvent<HTMLDivElement>) {
    if (!drawing || saving || !config.enabled) return;
    const point = canvasPoint(event);
    if (
      draftPoints.length >= 3 &&
      Math.hypot(point.x - draftPoints[0].x, point.y - draftPoints[0].y) <= CLOSE_POINT_DISTANCE
    ) {
      finishDrawing();
      return;
    }
    if (draftPoints.length >= MAX_POLYGON_POINTS) {
      setError(true);
      setMessage(`A detection area supports up to ${MAX_POLYGON_POINTS} points.`);
      return;
    }
    setDraftPoints((current) => [...current, point]);
    setError(false);
  }
  function movePolygonPointer(event: React.PointerEvent<HTMLDivElement>) {
    if (drawing) setHoverPoint(canvasPoint(event));
  }
  function undoPolygonPoint() {
    setDraftPoints((current) => current.slice(0, -1));
    setError(false);
  }
  function clearPolygonPoints() {
    setDraftPoints([]);
    setHoverPoint(null);
    setError(false);
  }
  function updateMask(maskId: string, changes: Partial<Pick<AnalyticsMaskBase, "name" | "objectTypes" | "loiteringSeconds">>) {
    setConfig((current) => {
      const masks = current.masks.map((mask) => mask.id === maskId ? { ...mask, ...changes } : mask);
      const objectTypes = ANALYTICS_OBJECT_OPTIONS.filter((option) => masks.some((mask) => mask.objectTypes.includes(option.value))).map((option) => option.value);
      return { ...current, masks, objectTypes };
    });
  }
  function toggleMask(maskId: string) {
    setConfig((current) => ({ ...current, selectedMaskIds: current.selectedMaskIds.includes(maskId) ? current.selectedMaskIds.filter((id) => id !== maskId) : [...current.selectedMaskIds, maskId] }));
  }
  function deleteMask(maskId: string) {
    setConfig((current) => {
      const masks = current.masks.filter((mask) => mask.id !== maskId);
      const objectTypes = ANALYTICS_OBJECT_OPTIONS.filter((option) => masks.some((mask) => mask.objectTypes.includes(option.value))).map((option) => option.value);
      return { ...current, masks, objectTypes, selectedMaskIds: current.selectedMaskIds.filter((id) => id !== maskId) };
    });
  }

  async function save() {
    if (saving || !isDirty || drawing) return;
    if (validationMessage) { setError(true); setMessage(validationMessage); return; }
    setSaving(true); setError(false); setMessage("Saving analytics settings...");
    try {
      const token = await getIdToken();
      const response = await fetch(`${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`, { method: "PATCH", headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ analyticsConfig: config }) });
      const { text, json } = await readResponse(response);
      if (!response.ok) throw new Error(json?.message || json?.error || text || response.statusText);
      const persisted = normalizeAnalyticsConfig(json?.camera?.analyticsConfig || config);
      setConfig(persisted); setSaved(persisted);
      onUpdateCamera({ ...camera, ...(json?.camera || {}), analyticsConfig: persisted });
      setMessage(`Saved ${persisted.selectedMaskIds.length} active area${persisted.selectedMaskIds.length === 1 ? "" : "s"} with independent object rules.`);
    } catch (cause) {
      setError(true); setMessage(`Unable to save analytics settings: ${cause instanceof Error ? cause.message : "Unexpected error"}`);
    } finally { setSaving(false); }
  }

  const draftLinePoints = draftPoints.length
    ? [...draftPoints, ...(hoverPoint ? [hoverPoint] : [])]
    : [];

  return (
    <section style={{ display: "grid", gap: 18, padding: 22, border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff" }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#2563eb", fontSize: 11, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>Cloud analytics</div>
          <h3 style={{ margin: "5px 0 7px", color: "#0f172a" }}>Object detection areas</h3>
          <p style={{ margin: 0, maxWidth: 780, color: "#64748b", lineHeight: 1.55 }}>Each area can detect different objects. Draw flexible polygons around exact regions and assign Person, Car, Bicycle, Boat, Animal, or Loitering rules independently per area.</p>
        </div>
        <label style={{ ...buttonStyle, display: "inline-flex", alignItems: "center", gap: 9, background: "#f8fafc" }}>
          <input type="checkbox" role="switch" checked={config.enabled} disabled={saving || drawing} onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))} /> Enable detection
        </label>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, .8fr) minmax(320px, 1.4fr)", gap: 18, opacity: config.enabled ? 1 : 0.58 }}>
        <aside style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <div style={{ display: "grid", gap: 10, padding: 14, border: "1px solid #dbeafe", borderRadius: 12, background: "#eff6ff" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <label htmlFor="analytics-confidence" style={{ color: "#1d4ed8", fontSize: 12, fontWeight: 900 }}>Detection confidence</label>
              <strong style={{ color: "#0f172a", fontSize: 14 }}>{config.confidenceThreshold}%</strong>
            </div>
            <input
              id="analytics-confidence"
              aria-label="Detection confidence"
              type="range"
              min={MIN_CONFIDENCE_THRESHOLD}
              max={MAX_CONFIDENCE_THRESHOLD}
              step={1}
              value={config.confidenceThreshold}
              disabled={!config.enabled || saving || drawing}
              onChange={(event) => setConfig((current) => ({
                ...current,
                confidenceThreshold: clamp(
                  Math.round(Number(event.target.value)),
                  MIN_CONFIDENCE_THRESHOLD,
                  MAX_CONFIDENCE_THRESHOLD,
                ),
              }))}
            />
            <div style={{ color: "#64748b", fontSize: 11 }}>25% detects more objects. Higher values reduce false detections. This setting applies only to this camera.</div>
          </div>
          <div style={{ padding: 14, border: "1px solid #dbeafe", borderRadius: 12, background: "#eff6ff" }}>
            <strong style={{ display: "block", marginBottom: 8, color: "#1d4ed8", fontSize: 12 }}>Defaults for new areas</strong>
            <ObjectChips values={defaultObjects} disabled={!config.enabled || saving || drawing} onChange={setDefaultObjects} />
            <div style={{ marginTop: 8, color: "#64748b", fontSize: 11 }}>Copied only when you draw a new area. Every area can be changed independently afterward.</div>
          </div>
          {drawing ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" disabled={!draftPoints.length} onClick={undoPolygonPoint} style={{ ...buttonStyle, opacity: draftPoints.length ? 1 : 0.5 }}>Undo point</button>
                <button type="button" disabled={draftPoints.length < 3} onClick={finishDrawing} style={{ ...buttonStyle, borderColor: "#2563eb", color: "#fff", background: "#2563eb", opacity: draftPoints.length >= 3 ? 1 : 0.5 }}>Finish area</button>
                <button type="button" disabled={!draftPoints.length} onClick={clearPolygonPoints} style={{ ...buttonStyle, opacity: draftPoints.length ? 1 : 0.5 }}>Clear</button>
                <button type="button" onClick={cancelDrawing} style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fef2f2" }}>Cancel drawing</button>
              </div>
              <div style={{ color: "#64748b", fontSize: 11 }}>{draftPoints.length}/{MAX_POLYGON_POINTS} points · click near the first point to close.</div>
            </div>
          ) : (
            <button type="button" disabled={!config.enabled || saving || config.masks.length >= MAX_MASKS} onClick={() => startDrawing()} style={{ ...buttonStyle, borderColor: "#93c5fd", color: "#1d4ed8", background: "#eff6ff" }}>+ Draw area</button>
          )}
          <div style={{ color: "#64748b", fontSize: 11 }}>{config.masks.length}/{MAX_MASKS} areas created</div>
        </aside>

        <div data-testid="analytics-mask-canvas" onPointerDown={addPolygonPoint} onPointerMove={movePolygonPointer} onPointerLeave={() => setHoverPoint(null)} style={{ position: "relative", width: "100%", aspectRatio, overflow: "hidden", border: `2px solid ${drawing ? "#2563eb" : "#cbd5e1"}`, borderRadius: 14, background: "#e2e8f0", cursor: drawing ? "crosshair" : "default", userSelect: "none", touchAction: "none" }}>
          {camera.thumbnailUrl ? <img src={camera.thumbnailUrl} alt={`${camera.name || "Camera"} analytics preview`} draggable={false} onLoad={(event) => { const image = event.currentTarget; if (image.naturalWidth && image.naturalHeight) setAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`); }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", pointerEvents: "none" }} /> : <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#64748b", fontSize: 12 }}>Latest camera thumbnail is unavailable</div>}
          <svg aria-hidden="true" viewBox="0 0 1000 1000" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
            {config.masks.map((mask) => {
              if (drawing && redrawingMaskId === mask.id) return null;
              const active = selectedSet.has(mask.id);
              const stroke = active ? "#22c55e" : "#94a3b8";
              const fill = active ? "rgba(34,197,94,.18)" : "rgba(100,116,139,.16)";
              return "type" in mask && mask.type === "polygon" ? (
                <polygon key={mask.id} data-testid={`analytics-polygon-${mask.id}`} points={svgPoints(mask.points)} fill={fill} stroke={stroke} strokeWidth="5" vectorEffect="non-scaling-stroke" />
              ) : (
                <rect key={mask.id} data-testid={`analytics-rectangle-${mask.id}`} x={mask.x * 1000} y={mask.y * 1000} width={mask.width * 1000} height={mask.height * 1000} fill={fill} stroke={stroke} strokeWidth="5" vectorEffect="non-scaling-stroke" />
              );
            })}
            {draftPoints.length >= 3 && <polygon data-testid="analytics-draft-fill" points={svgPoints(draftPoints)} fill="rgba(37,99,235,.14)" stroke="none" />}
            {draftLinePoints.length > 0 && <polyline data-testid="analytics-draft-line" points={svgPoints(draftLinePoints)} fill="none" stroke="#2563eb" strokeWidth="5" strokeDasharray="12 8" vectorEffect="non-scaling-stroke" />}
            {draftPoints.map((point, index) => <circle key={`${point.x}-${point.y}-${index}`} cx={point.x * 1000} cy={point.y * 1000} r={index === 0 ? 11 : 8} fill={index === 0 ? "#1d4ed8" : "#fff"} stroke="#2563eb" strokeWidth="4" vectorEffect="non-scaling-stroke" />)}
          </svg>
          {config.masks.map((mask, index) => {
            if (drawing && redrawingMaskId === mask.id) return null;
            return <span key={`label-${mask.id}`} title={`${mask.name}: ${mask.objectTypes.join(", ") || "No objects"}`} style={{ position: "absolute", left: `calc(${mask.x * 100}% + 5px)`, top: `calc(${mask.y * 100}% + 5px)`, padding: "2px 6px", borderRadius: 6, color: "#fff", background: "rgba(15,23,42,.82)", fontSize: 10, fontWeight: 900, pointerEvents: "none" }}>{index + 1} · {mask.objectTypes.map((value) => ANALYTICS_OBJECT_OPTIONS.find((option) => option.value === value)?.label || value).join(" + ") || "No objects"}</span>;
          })}
        </div>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}><strong style={{ color: "#0f172a", fontSize: 13 }}>Area rules</strong><span style={{ color: "#64748b", fontSize: 11 }}>Each area has independent objects.</span></div>
        {config.masks.length === 0 ? <div style={{ padding: 18, border: "1px dashed #cbd5e1", borderRadius: 12, color: "#64748b", textAlign: "center", fontSize: 12 }}>Draw your first area on the camera image.</div> : config.masks.map((mask, index) => (
          <article key={mask.id} style={{ display: "grid", gridTemplateColumns: "auto minmax(150px, .8fr) minmax(260px, 1.4fr) minmax(135px, .55fr) auto", gap: 12, alignItems: "center", padding: 12, border: `1px solid ${selectedSet.has(mask.id) ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 12, background: selectedSet.has(mask.id) ? "#f8fbff" : "#f8fafc" }}>
            <label style={{ display: "grid", justifyItems: "center", gap: 3, color: "#64748b", fontSize: 10, fontWeight: 800 }}><input type="checkbox" checked={selectedSet.has(mask.id)} disabled={saving || drawing} onChange={() => toggleMask(mask.id)} /> Active</label>
            <input aria-label={`Area ${index + 1} name`} value={mask.name} maxLength={80} disabled={saving || drawing} onChange={(event) => updateMask(mask.id, { name: event.target.value })} style={{ minHeight: 38, padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 9, font: "inherit", fontSize: 12, fontWeight: 700 }} />
            <ObjectChips values={mask.objectTypes} disabled={saving || drawing} onChange={(objectTypes) => updateMask(mask.id, { objectTypes })} />
            <label style={{ display: "grid", gap: 4, color: "#475569", fontSize: 10, fontWeight: 800, opacity: mask.objectTypes.includes("loitering") ? 1 : 0.45 }}>
              Loitering after
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input aria-label={`Area ${index + 1} loitering duration`} type="number" min={MIN_LOITERING_SECONDS} max={MAX_LOITERING_SECONDS} step={5} value={mask.loiteringSeconds} disabled={saving || drawing || !mask.objectTypes.includes("loitering")} onChange={(event) => updateMask(mask.id, { loiteringSeconds: clamp(Math.round(Number(event.target.value) || DEFAULT_LOITERING_SECONDS), MIN_LOITERING_SECONDS, MAX_LOITERING_SECONDS) })} style={{ width: 78, minHeight: 34, padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 8 }} />
                sec
              </span>
            </label>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button type="button" disabled={saving || drawing} onClick={() => startDrawing(mask.id)} style={{ ...buttonStyle, color: "#1d4ed8", borderColor: "#bfdbfe", background: "#eff6ff" }}>Redraw area</button>
              <button type="button" disabled={saving || drawing} onClick={() => deleteMask(mask.id)} style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }}>Delete</button>
            </div>
          </article>
        ))}
      </div>

      {message && <div role={error ? "alert" : "status"} style={{ padding: "10px 12px", border: `1px solid ${error ? "#fecaca" : "#bbf7d0"}`, borderRadius: 10, color: error ? "#b91c1c" : "#166534", background: error ? "#fef2f2" : "#f0fdf4", fontSize: 12, fontWeight: 700 }}>{message}</div>}
      <footer style={{ display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center" }}>
        {validationMessage && config.enabled && <span style={{ marginRight: "auto", color: "#b45309", fontSize: 11, fontWeight: 700 }}>{validationMessage}</span>}
        <button type="button" disabled={!isDirty || saving || drawing} onClick={() => { setConfig(saved); cancelDrawing(); setMessage(""); setError(false); }} style={{ ...buttonStyle, opacity: !isDirty || saving || drawing ? 0.5 : 1 }}>Reset</button>
        <button type="button" disabled={!isDirty || saving || drawing} onClick={() => void save()} style={{ ...buttonStyle, borderColor: "#2563eb", color: "#fff", background: "#2563eb", opacity: !isDirty || saving || drawing ? 0.55 : 1 }}>{saving ? "Saving..." : "Save analytics"}</button>
      </footer>
    </section>
  );
}
